import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft, BadgeCheck, MapPin, Clock, Users, Star, IndianRupee,
  Mail, Globe, Phone, Send, MessageSquare, CheckCircle2, XCircle,
  Loader2, Briefcase, Hash, CalendarCheck, Quote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface CAProfile {
  id: string;
  name: string;
  firm_name: string | null;
  city: string;
  state: string | null;
  experience_years: number;
  specializations: string[];
  services: string[];
  pricing_hourly: number | null;
  pricing_monthly: number | null;
  bio: string | null;
  email: string | null;
  is_verified: boolean;
  avg_rating: number;
  total_reviews: number;
  total_clients: number;
  avatar_url: string | null;
  membership_number?: string | null;
  availability?: string | null;
  phone?: string | null;
  website?: string | null;
}

interface ServiceRequest {
  id: string;
  ca_profile_id: string;
  requester_user_id: string;
  company_name: string;
  service_type: string;
  description: string | null;
  budget: string | null;
  status: string;
  created_at: string;
}

interface Message {
  id: string;
  service_request_id: string;
  sender_id: string;
  content: string;
  is_read: boolean;
  created_at: string;
}

interface Review {
  id: string;
  ca_profile_id: string;
  reviewer_user_id: string;
  rating: number;
  review_text: string | null;
  created_at: string;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`h-4 w-4 ${s <= Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`} />
      ))}
      <span className="text-sm font-semibold text-foreground ml-1.5">{rating.toFixed(1)}</span>
      <span className="text-xs text-muted-foreground ml-0.5">({rating > 0 ? "verified" : "no reviews"})</span>
    </div>
  );
}

function InteractiveStarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`h-5 w-5 cursor-pointer transition-colors ${s <= value ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30 hover:text-yellow-300"}`}
          onClick={() => onChange(s)}
        />
      ))}
    </div>
  );
}

function AvailabilityBadge({ availability }: { availability?: string | null }) {
  const map: Record<string, { label: string; cls: string }> = {
    available: { label: "Available", cls: "bg-green-500/10 text-green-600 border-green-500/20" },
    busy: { label: "Busy", cls: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
    unavailable: { label: "Unavailable", cls: "bg-red-500/10 text-red-600 border-red-500/20" },
  };
  const info = map[availability || "available"] || map.available;
  return <Badge variant="outline" className={`text-[10px] ${info.cls}`}>{info.label}</Badge>;
}

// Chat Component
function ChatPanel({ request, userId }: { request: ServiceRequest; userId: string }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchMessages();
    const channel = supabase
      .channel(`messages-${request.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "marketplace_messages", filter: `service_request_id=eq.${request.id}` }, (payload) => {
        setMessages((prev) => [...prev, payload.new as Message]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [request.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const fetchMessages = async () => {
    const { data } = await supabase
      .from("marketplace_messages")
      .select("*")
      .eq("service_request_id", request.id)
      .order("created_at", { ascending: true });
    if (data) setMessages(data as Message[]);
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    const { error } = await supabase.from("marketplace_messages").insert({
      service_request_id: request.id,
      sender_id: userId,
      content: newMsg.trim(),
    });
    if (!error) setNewMsg("");
    setSending(false);
  };

  return (
    <div className="flex flex-col h-[350px]">
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-2 p-3">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-xs text-muted-foreground">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender_id === userId ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-xl px-3 py-2 text-xs ${msg.sender_id === userId ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                <p>{msg.content}</p>
                <p className={`text-[9px] mt-1 ${msg.sender_id === userId ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                  {new Date(msg.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="border-t border-border p-2 flex gap-2">
        <Input
          value={newMsg}
          onChange={(e) => setNewMsg(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type a message..."
          className="text-xs h-8"
        />
        <Button size="sm" className="h-8 px-3" onClick={sendMessage} disabled={!newMsg.trim() || sending}>
          <Send className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

// Reviews Panel
function ReviewsPanel({ caId, avgRating, totalReviews }: { caId: string; avgRating: number; totalReviews: number }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, [caId]);

  const fetchReviews = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("ca_reviews")
      .select("*")
      .eq("ca_profile_id", caId)
      .order("created_at", { ascending: false });
    if (data) setReviews(data as Review[]);
    setLoading(false);
  };

  const submitReview = async () => {
    if (!user) {
      toast({ title: "Please log in to leave a review", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("ca_reviews").insert({
      ca_profile_id: caId,
      reviewer_user_id: user.id,
      rating,
      review_text: reviewText.trim() || null,
    });
    if (error) {
      toast({ title: "Failed to submit review", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Review submitted!" });
      setShowForm(false);
      setRating(5);
      setReviewText("");
      fetchReviews();
    }
    setSubmitting(false);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-3xl font-bold text-foreground">{avgRating.toFixed(1)}</span>
          <div>
            <StarRating rating={avgRating} />
            <p className="text-xs text-muted-foreground">{totalReviews} review{totalReviews !== 1 ? "s" : ""}</p>
          </div>
        </div>
        {user && !showForm && (
          <Button size="sm" variant="outline" className="text-xs gap-1" onClick={() => setShowForm(true)}>
            <Star className="h-3 w-3" /> Write Review
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="bg-muted/30 border-border">
          <CardContent className="p-4 space-y-3">
            <div>
              <p className="text-xs font-medium text-foreground mb-1.5">Your Rating</p>
              <InteractiveStarRating value={rating} onChange={setRating} />
            </div>
            <Textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience working with this CA..."
              rows={3}
              className="text-xs"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={submitReview} disabled={submitting} className="text-xs gap-1">
                {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                Submit
              </Button>
              <Button size="sm" variant="ghost" className="text-xs" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-10">
          <Quote className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">No testimonials yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <Card key={r.id} className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`h-3 w-3 ${s <= r.rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/20"}`} />
                    ))}
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </div>
                {r.review_text && (
                  <p className="text-xs text-muted-foreground leading-relaxed italic">"{r.review_text}"</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function CAProfileDetail({ ca, onBack }: { ca: CAProfile; onBack: () => void }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");

  useEffect(() => {
    if (user) fetchRequests();
  }, [user, ca.id]);

  const fetchRequests = async () => {
    const { data } = await supabase
      .from("service_requests")
      .select("*")
      .eq("ca_profile_id", ca.id)
      .order("created_at", { ascending: false });
    if (data) setRequests(data as ServiceRequest[]);
  };

  const sendRequest = async () => {
    if (!user || !companyName.trim() || !serviceType) {
      toast({ title: "Please fill all required fields", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await supabase.from("service_requests").insert({
      ca_profile_id: ca.id,
      requester_user_id: user.id,
      company_name: companyName.trim(),
      service_type: serviceType,
      description: description.trim() || null,
      budget: budget || null,
    });
    if (error) {
      toast({ title: "Failed to send request", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Request sent!", description: `Your request has been sent to ${ca.name}` });
      setShowRequestDialog(false);
      setCompanyName(""); setServiceType(""); setDescription(""); setBudget("");
      fetchRequests();
    }
    setLoading(false);
  };

  const updateRequestStatus = async (reqId: string, status: string) => {
    const { error } = await supabase.from("service_requests").update({ status }).eq("id", reqId);
    if (!error) {
      toast({ title: `Request ${status}` });
      fetchRequests();
    }
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      pending: "bg-yellow-500/10 text-yellow-600",
      accepted: "bg-green-500/10 text-green-600",
      declined: "bg-destructive/10 text-destructive",
      completed: "bg-accent/10 text-accent",
      cancelled: "bg-muted text-muted-foreground",
    };
    return map[status] || "bg-muted text-muted-foreground";
  };

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Marketplace
      </Button>

      {/* Profile Header */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {ca.avatar_url ? (
                <img src={ca.avatar_url} alt={ca.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-primary font-bold text-3xl">{ca.name.charAt(0)}</span>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="text-xl font-bold text-foreground">{ca.name}</h1>
                {ca.is_verified && (
                  <Badge className="bg-accent/10 text-accent border-accent/20 text-[10px] gap-1">
                    <BadgeCheck className="h-3 w-3" /> Verified
                  </Badge>
                )}
                <AvailabilityBadge availability={ca.availability} />
              </div>
              {ca.firm_name && <p className="text-sm text-muted-foreground mb-1">{ca.firm_name}</p>}
              {ca.membership_number && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                  <Hash className="h-3 w-3" /> {ca.membership_number}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 mb-3">
                <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {ca.city}{ca.state ? `, ${ca.state}` : ""}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {ca.experience_years} years experience</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {ca.total_clients} clients</span>
              </div>

              <StarRating rating={ca.avg_rating} />

              {ca.bio && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{ca.bio}</p>}
            </div>

            {/* Action + Pricing */}
            <div className="flex flex-col gap-3 md:min-w-[200px]">
              <Card className="bg-muted/30 border-border">
                <CardContent className="p-3 space-y-2">
                  {ca.pricing_hourly && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Hourly</span>
                      <span className="text-sm font-semibold text-foreground flex items-center"><IndianRupee className="h-3 w-3" />{ca.pricing_hourly.toLocaleString("en-IN")}</span>
                    </div>
                  )}
                  {ca.pricing_monthly && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Monthly</span>
                      <span className="text-sm font-semibold text-foreground flex items-center"><IndianRupee className="h-3 w-3" />{ca.pricing_monthly.toLocaleString("en-IN")}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
                <DialogTrigger asChild>
                  <Button className="w-full gap-1.5">
                    <Briefcase className="h-4 w-4" /> Send Request
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Send Service Request to {ca.name}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">Company Name *</label>
                      <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Your company name" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">Service Required *</label>
                      <Select value={serviceType} onValueChange={setServiceType}>
                        <SelectTrigger><SelectValue placeholder="Select service" /></SelectTrigger>
                        <SelectContent>
                          {ca.services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">Description</label>
                      <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe your requirements..." rows={3} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">Budget Range</label>
                      <Select value={budget} onValueChange={setBudget}>
                        <SelectTrigger><SelectValue placeholder="Select budget" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Under ₹10,000">Under ₹10,000</SelectItem>
                          <SelectItem value="₹10,000 - ₹25,000">₹10,000 - ₹25,000</SelectItem>
                          <SelectItem value="₹25,000 - ₹50,000">₹25,000 - ₹50,000</SelectItem>
                          <SelectItem value="₹50,000+">₹50,000+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={sendRequest} disabled={loading} className="w-full">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
                      Send Request
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <div className="flex flex-col gap-1.5">
                {ca.email && (
                  <a href={`mailto:${ca.email}`} className="text-xs text-accent hover:underline flex items-center gap-1">
                    <Mail className="h-3 w-3" /> {ca.email}
                  </a>
                )}
                {ca.phone && (
                  <a href={`tel:${ca.phone}`} className="text-xs text-accent hover:underline flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {ca.phone}
                  </a>
                )}
                {ca.website && (
                  <a href={ca.website} target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline flex items-center gap-1">
                    <Globe className="h-3 w-3" /> Website
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="services">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="reviews">Reviews ({ca.total_reviews})</TabsTrigger>
          <TabsTrigger value="requests">Requests ({requests.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="services" className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="bg-card border-border">
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-xs font-semibold">Specializations</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0 flex flex-wrap gap-1.5">
                {ca.specializations.map((s) => (
                  <Badge key={s} className="bg-accent/10 text-accent border-accent/20 text-[10px]">{s}</Badge>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-xs font-semibold">Services Offered</CardTitle>
              </CardHeader>
              <CardContent className="p-3 pt-0">
                <div className="space-y-1">
                  {ca.services.map((s) => (
                    <div key={s} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3 text-green-500" /> {s}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          <ReviewsPanel caId={ca.id} avgRating={ca.avg_rating} totalReviews={ca.total_reviews} />
        </TabsContent>

        <TabsContent value="requests" className="space-y-3">
          {requests.length === 0 ? (
            <div className="text-center py-10">
              <Briefcase className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">No service requests yet</p>
            </div>
          ) : (
            requests.map((req) => (
              <Card key={req.id} className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-foreground">{req.service_type}</h4>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium capitalize ${statusBadge(req.status)}`}>
                          {req.status}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{req.company_name} • {new Date(req.created_at).toLocaleDateString("en-IN")}</p>
                    </div>
                    {req.status === "pending" && (
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => updateRequestStatus(req.id, "accepted")}>
                          <CheckCircle2 className="h-3 w-3" /> Accept
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-destructive" onClick={() => updateRequestStatus(req.id, "declined")}>
                          <XCircle className="h-3 w-3" /> Decline
                        </Button>
                      </div>
                    )}
                  </div>
                  {req.description && <p className="text-xs text-muted-foreground mb-2">{req.description}</p>}
                  {req.budget && <Badge variant="outline" className="text-[10px]"><IndianRupee className="h-2.5 w-2.5 mr-0.5" /> {req.budget}</Badge>}

                  {req.status === "accepted" && user && (
                    <div className="mt-3 border-t border-border/30 pt-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <MessageSquare className="h-3.5 w-3.5 text-accent" />
                        <span className="text-xs font-semibold text-foreground">Messages</span>
                      </div>
                      <ChatPanel request={req} userId={user.id} />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
